import { Link } from 'react-router-dom';
import { AlertTriangle, Boxes, CheckCircle, Server } from 'lucide-react';
import { PageHeader, StatsGrid, StatCard, LoadingState, ErrorState, StatusBadge } from '../../components/common';
import { useObservationTime } from '@/hooks/useObservationTime';
import { useContainerAssignmentStatuses } from '../../hooks/useContainerProfiles';
import { engineConfigurationAction, engineConfigurationState, engineEmptyStateMessage } from '@/lib/engine-list-presentation';
import { evaluateEngineRuntime, runtimeAdmissionLabel, runtimeAdmissionStatus } from '@/lib/runtime-admission-presentation';
import { useAuth } from '@/contexts/AuthContext';

export function ContainersPage() {
  const { user } = useAuth();
  const now = useObservationTime();
  const containers = useContainerAssignmentStatuses();
  const rows = (containers.data ?? []).filter(row => row.container.serviceType === 'SipEngine');
  const unassigned = rows.filter((row) => row.assignment === null).length;

  if (user?.role !== 'admin') return <ErrorState title="SIP Engines unavailable" message="SIP engine configuration and runtime observations require administrator access." />;
  if (containers.isLoading) return <LoadingState />;
  if (containers.error) {
    return <ErrorState title="Failed to load containers" message={containers.error instanceof Error ? containers.error.message : 'Unknown error'} onRetry={() => containers.refetch()} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="SIP Engines"
        description="Deploy externally, then configure SIP engine identities here. Saved assignments and runtime readiness are shown separately. Refreshes every 15 seconds."
        onRefresh={() => containers.refetch()}
        isRefreshing={containers.isFetching}
      />

      <Link className="inline-block text-sm text-blue-700" to="/system/container-profiles">Shared engine configurations (advanced) →</Link>

      {unassigned > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 flex gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">{unassigned} unassigned SIP engine{unassigned === 1 ? '' : 's'} need configuration</p>
            <p className="text-sm">Open Configure to use the existing logical identity. This does not start or deploy a process; the detail view distinguishes registered runtime observation from a prepared-only identity.</p>
          </div>
        </div>
      )}

      <StatsGrid columns={3}>
        <StatCard label="Stored identities" value={rows.length} icon={Server} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard label="Assigned in database" value={rows.length - unassigned} icon={CheckCircle} iconBgColor="bg-gray-100" iconColor="text-gray-600" />
        <StatCard label="Unassigned" value={unassigned} icon={AlertTriangle} iconBgColor="bg-amber-100" iconColor="text-amber-600" />
      </StatsGrid>

      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">SIP engine identity</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Service / status</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Heartbeat</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Profile</th>
            <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((row) => (
              <tr key={row.container.containerId} className="hover:bg-gray-50">
                 <td className="px-6 py-4"><div className="font-mono text-sm text-gray-900">{row.container.containerId}</div><div className="text-xs text-gray-500">{row.container.ipAddress}:{row.container.port}</div></td>
                <td className="px-6 py-4"><div className="text-sm">{row.container.serviceType}</div><span className="text-sm text-gray-600">{engineConfigurationState(row.assignment !== null) === 'configured' ? `Reported: ${row.container.status}` : 'Needs configuration'}</span></td>
                <td className="px-6 py-4 text-sm">{row.assignment ? (() => { const admission = evaluateEngineRuntime({ serviceType: row.container.serviceType, status: row.container.status, port: row.container.port, lastHeartbeat: row.container.lastHeartbeat, containerData: row.container.containerData }, now); const label = row.profile?.enabled === false ? 'Review configuration' : runtimeAdmissionLabel(admission); return <><StatusBadge status={row.profile?.enabled === false ? 'warning' : runtimeAdmissionStatus(admission)} label={label} /><div className="text-xs text-gray-500">Last report: {Number.isFinite(Date.parse(row.container.lastHeartbeat)) ? new Date(row.container.lastHeartbeat).toLocaleString() : 'Unknown'}</div></>; })() : <><StatusBadge status="warning" label="Prepared / not reported" /><div className="text-xs text-gray-500">No saved assignment</div></>}</td>
                <td className="px-6 py-4">
                  {row.profile ? <span>{row.profile.name}<div className="text-xs text-gray-500">{row.profile.enabled ? 'Saved assignment' : 'Profile disabled — unavailable to engine'}</div></span> : <span className="text-amber-800">Unassigned</span>}
                </td>
                <td className="px-6 py-4 text-right"><Link className="text-blue-600 hover:text-blue-800" to={`/system/containers/${encodeURIComponent(row.container.containerId)}`}>{engineConfigurationAction(row.assignment !== null)}</Link></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500"><Boxes className="mx-auto h-8 w-8 mb-2" />{engineEmptyStateMessage()}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">Healthy observation requires a healthy lifecycle and heartbeat less than two minutes old. Saved assignments do not confirm engine application or call readiness.</p>
    </div>
  );
}
